/**
 * fairsform.js — Fuar Oluştur / Düzenle
 * Route: /Management/Fairs/new · /Management/Fairs/:id · /Management/Fairs/:id/edit
 *
 * CANLI: `GET /fuar/{id}/detay-new`, `POST /fuar/add-new`, `PUT /fuar/update-new/{id}`,
 * `POST /fuar/logo`, `GET /fuar/listFuarAlani`, `GET /fuar/series/list` (+ seri CRUD,
 * FuarSeriesModal). Sözleşme backend kaynağından okundu: util/knowledgebase/fuar-seri-ve-talep-turleri.md.
 *
 * Bölüm sırası (backlog #20, tarih kartları #47'de yeniden gruplandı): Genel Bilgiler (logo
 * dahil) → Fuar Alanı → Fuar Tarih ve Saat Bilgileri (Fuar / Kurulum / Söküm alt grupları) →
 * Belge Kabul Tarih ve Saat Bilgileri (Sistem İçi = Belge Kabul, Sistem Dışı = dijital dönem) →
 * Fuar Firmaları → Fuar Serisi.
 *
 * ⚠️ "Kısa Ad" formda yok — backend kısa adı her kayıtta fuar adının kendisi olarak yazıyor.
 * ⚠️ Logo önce `POST /fuar/logo` ile yüklenir, dönen ad `fuarLogoFile` olarak gider (#18).
 *    Yeni fuarda zorunlu; düzenlemede gönderilmezse mevcut logo korunur.
 * ⚠️ Sekiz tarih alanının hepsi zorunlu (boşsa backend 400). Açılış/kapanış, kurulum, söküm
 *    saatleri ve dijital belge kabul süresi (#17) isteğe bağlı: düzenlemede yalnızca DEĞİŞENLER
 *    gönderiliyor — gönderilmeyen alan backend'de saklı değeriyle (saniyesiyle) kalır, boş
 *    gönderilen temizlenir.
 * ⚠️ `fuarReportActive` KALDIRILDI (#42) — form artık göndermiyor; backend New'de 0 varsayıyor,
 *    Update'te dokunmadan bırakıyor.
 * ⚠️ `fuarSeriesId` göndermemek fuarı SERİDEN AYIRIR; firma rol listeleri ise her kayıtta
 *    ekrandaki haliyle gönderiliyor (#21).
 * ⚠️ #718 (backend #717): Kurulum'un altında Katılımcı / Dekoratör / Tedarikçi için isteğe bağlı birer kurulum penceresi.
 *    Satırlar detay-new'in `kurulumRolSaatleri`nden kurulur (dördüncü rol — Taşeron — listeye kendiliğinden düşer); yeni
 *    fuarda henüz detay yok, 1/3/16 yedek listesiyle başlar. Fuar kaydedildikten SONRA, yalnızca DEĞİŞEN roller
 *    PUT /fuar/{id}/kurulum-saatleri ile yazılır (fuarSlice). Bir rolün boş penceresi fuarın Kurulum'unu kullanır. Tedarikçi
 *    (firma_type 16) Fuar Firmaları'nda `tedarikciIds` / `tedarikciCompanies` ile, diğer servis listeleri gibi.
 */
import React, { useState, useEffect, useCallback, useContext } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Container, Row, Col, Card, CardBody, CardHeader,
    Button, Input, Label, FormGroup, FormFeedback, Spinner, Badge, Alert,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TarihGirdisi from "../../../Components/Common/TarihGirdisi";
import KontrolluSilDugmesi from "../../../Components/Common/KontrolluSilDugmesi";
import MainContext from "../../../Components/Contexts/MainContext";
import FuarSeriesModal from "../../../Components/Fairs/FuarSeriesModal";
import FuarLogoImg from "../../../Components/Fairs/FuarLogoImg";
import FirmaSearchSelect from "../../../Components/Firma/FirmaSearchSelect";
import FuarSalonlariSecici from "../../../Components/Fairs/FuarSalonlariSecici";
import { createFuar, updateFuar, fetchFuarDetail, clearError, clearSuccess, clearKurulumHata } from "../../../slices/fuarSlice";
import { fuarService, apiErrorMessage, LANGUAGE } from "../../../services/fuarService";
import { rolFormu, pencereSorunu, degisenRoller } from "../../../services/fuarKurulumSaatiService";
import { tarihSaatMetni } from "../../../Components/Fairs/fuarAsamalari";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

// Backend'in POST /fuar/logo kuralları (FuarLogoConstant) — yüklemeden önce burada da bakılıyor.
const LOGO_EXTENSIONS = ["png", "jpg", "jpeg", "webp"];
const LOGO_MAX_BYTES = 5 * 1024 * 1024;
const LOGO_ACCEPT = ".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp";

const LOGO_SOURCE_LABELS = {
    get UPLOADED() { return i18n.t("yonetim:fairsform.yuklenmisLogo"); },
    get LEGACY() { return i18n.t("yonetim:fairsform.eskiSistemdenGelenLogo"); },
    get DEFAULT() { return i18n.t("yonetim:fairsform.logoYokVarsayilanGorselGosteriliyor"); },
};

// Backlog #17'nin isteğe bağlı alanları. Düzenlemede yalnızca değişenler gönderilir.
const OPTIONAL_DATETIME_FIELDS = [
    "fuarStartTime", "fuarEndTime",
    "fuarSetupStartTime", "fuarSetupEndTime",
    "fuarBreakStartTime", "fuarBreakEndTime",
    "fuarDigitalDocumentsStartDate", "fuarDigitalDocumentsStartTime",
    "fuarDigitalDocumentsEndDate", "fuarDigitalDocumentsEndTime",
];

const EMPTY_FORM = {
    fuarNameTr: "",
    fuarDescTr: "",
    fuarNameEn: "",
    fuarDescEn: "",
    fuarStartDate: "",
    fuarEndDate: "",
    fuarDocumentsStartDate: "",
    fuarDocumentsStartTime: "09:00",
    fuarDocumentsEndDate: "",
    fuarDocumentsEndTime: "18:00",
    fuarSetupStartDate: "",
    fuarSetupEndDate: "",
    fuarBreakStartDate: "",
    fuarBreakEndDate: "",
    ...Object.fromEntries(OPTIONAL_DATETIME_FIELDS.map((name) => [name, ""])),
    fuarAlaniId: "",
    fuarSeriesId: "",
};

// Tarih + saat çiftleri. Tarihler zorunlu; bitiş (saatiyle birlikte) başlangıçtan önce olamaz.
// fuar_break_* = söküm. `key` yalnızca #47'nin kart/alt-grup düzeni için render'da arama amaçlı —
// validate() ve submit hâlâ tüm diziyi sırayla dolaşıyor, davranış değişmedi.
const DATE_PAIRS = [
    {
        key: "fuar",
        // Backlog #97: heading above this pair already says "Fuar" — dropped the repeated
        // keyword from the label itself (Belge Kabul's pair keeps its own below, since its
        // heading is "Sistem İçi", not "Belge Kabul" — no repetition to drop there).
        start: { date: "fuarStartDate", time: "fuarStartTime", get label() { return i18n.t("yonetim:fairsform.acilisTarihi"); } },
        end: { date: "fuarEndDate", time: "fuarEndTime", get label() { return i18n.t("yonetim:fairsform.kapanisTarihi"); } },
        get orderError() { return i18n.t("yonetim:fairsform.fuarKapanisiAcilistanOnceOlamaz"); },
    },
    {
        key: "belgeKabul",
        start: { date: "fuarDocumentsStartDate", time: "fuarDocumentsStartTime", get label() { return i18n.t("yonetim:fairsform.belgeKabulBaslangici"); } },
        end: { date: "fuarDocumentsEndDate", time: "fuarDocumentsEndTime", get label() { return i18n.t("yonetim:fairsform.belgeKabulBitisi"); } },
        get orderError() { return i18n.t("yonetim:fairsform.belgeKabulBitisiBaslangicindanOnce"); },
    },
    {
        key: "kurulum",
        start: { date: "fuarSetupStartDate", time: "fuarSetupStartTime", get label() { return i18n.t("yonetim:fairsform.baslangic"); } },
        end: { date: "fuarSetupEndDate", time: "fuarSetupEndTime", get label() { return i18n.t("yonetim:fairsform.bitis"); } },
        get orderError() { return i18n.t("yonetim:fairsform.kurulumBitisiBaslangicindanOnceOlamaz"); },
    },
    {
        key: "sokum",
        start: { date: "fuarBreakStartDate", time: "fuarBreakStartTime", get label() { return i18n.t("yonetim:fairsform.baslangic"); } },
        end: { date: "fuarBreakEndDate", time: "fuarBreakEndTime", get label() { return i18n.t("yonetim:fairsform.bitis"); } },
        get orderError() { return i18n.t("yonetim:fairsform.sokumBitisiBaslangicindanOnceOlamaz"); },
    },
];
// Backlog #47: kart/alt-grup render'ı için anahtarla arama — DATE_PAIRS'in kendisi (ve validate()'in
// üzerinde dolaştığı sıra) değişmedi.
const DATE_PAIR_BY_KEY = Object.fromEntries(DATE_PAIRS.map((p) => [p.key, p]));

// WhatsApp, e-posta vb. ile gelen belgelerin kabul dönemi — isteğe bağlı, bilgi amaçlı.
const DIGITAL_PAIR = {
    start: { date: "fuarDigitalDocumentsStartDate", time: "fuarDigitalDocumentsStartTime", get label() { return i18n.t("yonetim:fairsform.baslangic"); } },
    end: { date: "fuarDigitalDocumentsEndDate", time: "fuarDigitalDocumentsEndTime", get label() { return i18n.t("yonetim:fairsform.bitis"); } },
    get orderError() { return i18n.t("yonetim:fairsform.dijitalBelgeKabulBitisiBaslangicindan"); },
};

// detay-new → firma listeleri, artık DÜZENLENEBİLİR (backlog #21, #22 üzerine kurulu).
// readKey: detay-new'in salt-okunur liste alanı. formKey: add-new/update-new'in kabul ettiği id
// listesi parametresi. firmaTypeId: #22'nin rol-sıralı aramasına VE firma_to_fuar.firma_type_id'ye
// gider — FuarService.getFuarDetayNew/updateFuarNew ile birebir aynı id'ler.
// Backlog #98 (Bilge, 2026-09-22): frontend label only — checked against live data, firma_type 12
// really is OPERASYON DESTEK (this label was wrong) and 11 really is PROJE DESTEK (firma_type_text
// itself is unchanged; "Proje" here is just a shorter on-screen name for the same type). No id or
// firma_type_text change.
const ROLE_GROUPS = [
    { readKey: "organizerCompanies", formKey: "organizatorIds", firmaTypeId: 2, get label() { return i18n.t("yonetim:fairsform.organizator"); } },
    { readKey: "isgCompanies", formKey: "isgIds", firmaTypeId: 13, get label() { return i18n.t("yonetim:fairsform.isg"); } },
    { readKey: "operasyonCompanies", formKey: "operasyonIds", firmaTypeId: 12, get label() { return i18n.t("yonetim:fairsform.operasyonDestek"); } },
    { readKey: "projeCompanies", formKey: "projeIds", firmaTypeId: 11, get label() { return i18n.t("yonetim:fairsform.proje"); } },
    { readKey: "temizlikCompanies", formKey: "temizlikIds", firmaTypeId: 7, get label() { return i18n.t("yonetim:fairsform.temizlik"); } },
    { readKey: "guvenlikCompanies", formKey: "guvenlikIds", firmaTypeId: 14, get label() { return i18n.t("yonetim:fairsform.guvenlik"); } },
    // #718 (backend #717): Tedarikçi / Supplier, a role of its own (firma_type 16, parent 0), matched to the fair like the others.
    { readKey: "tedarikciCompanies", formKey: "tedarikciIds", firmaTypeId: 16, get label() { return i18n.t("yonetim:fairsform.tedarikci"); } },
];

const EMPTY_COMPANY_LISTS = Object.fromEntries(ROLE_GROUPS.map((g) => [g.formKey, []]));

// #718: the roles that have a Kurulum window of their own today (backend KurulumSaatiConstant.ROLLER). Only a NEW fair uses this list — it has no
// detay-new yet; an existing fair builds the rows from detay-new's kurulumRolSaatleri, whose `rolAdi` comes in the request language.
const KURULUM_ROL_YEDEK_ANAHTARLARI = { 1: "rolKatilimci", 3: "rolDekorator", 16: "rolTedarikci" };
const yedekKurulumRolleri = () => Object.keys(KURULUM_ROL_YEDEK_ANAHTARLARI).map((id) => rolFormu({ firmaTypeId: id, tanimli: false }));
const kurulumRolAdi = (r) => r.rolAdi
    || (KURULUM_ROL_YEDEK_ANAHTARLARI[r.firmaTypeId] ? i18n.t(`yonetim:fairsform.${KURULUM_ROL_YEDEK_ANAHTARLARI[r.firmaTypeId]}`) : `#${r.firmaTypeId}`);

// detay-new'in ham satırları FirmaEntity'den geliyor, alan adları tutarsız gözleniyor — savunmacı
// eşleme (mevcut salt-okunur render de aynı üç adı deniyordu).
function normalizeCompanyRow(c) {
    return {
        firmaId: c.id ?? c.firmaId ?? c.firma_id,
        firmaName: c.firma_name || c.firmaName || "",
        firmaShortname: c.firma_shortname || c.firmaShortname || null,
        isForbidden: Number(c.isForbidden ?? c.firma_is_forbidden ?? c.is_forbidden ?? 0) === 1,
    };
}

// Backend saatleri HH:mm:ss, tarihleri yyyy-MM-dd veriyor; <input type="time"> HH:mm istiyor.
const toFormValue = (name, raw) => {
    if (!raw) return "";
    return name.endsWith("Time") ? String(raw).slice(0, 5) : String(raw);
};

// <input type="time"> HH:mm gösteriyor; backend HH:mm:ss saklıyor. Kullanıcı saate
// dokunmadıysa asıl değeri geri gönder — yoksa 23:59:59 her kayıtta 23:59:00'a iner.
const toTimeParam = (value, original) => {
    if (original && original.slice(0, 5) === value) return original;
    return `${value || "00:00"}:00`;
};

// Sıra karşılaştırması için "yyyy-MM-ddTHH:mm:ss". Backend'le aynı kural: saat yoksa başlangıç
// gün başı, bitiş gün sonu sayılır.
const toDateTimeKey = (date, time, isEnd) => {
    if (!time) return `${date}T${isEnd ? "23:59:59" : "00:00:00"}`;
    return `${date}T${time.length === 5 ? `${time}:00` : time}`;
};

const isEndBeforeStart = (form, { start, end }) =>
    toDateTimeKey(form[end.date], form[end.time], true) < toDateTimeKey(form[start.date], form[start.time], false);

const FairForm = () => {
  const { t: tx } = useTranslation("yonetim");
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { selectedFuarId } = useContext(MainContext);
    // "new" string'i gelirse create modu, sayısal id gelirse edit modu
    const numericId = id && id !== "new" ? parseInt(id, 10) : null;
    const isEditMode = Boolean(numericId && !isNaN(numericId));

    const { loading, detailLoading, error, success, selectedFuar, kurulumHata } = useSelector((state) => state.fuar);
    // Önceki bir fuarın detayı store'da kalmış olabilir — sadece BU fuarınkini kullan.
    const detail = isEditMode && Number(selectedFuar?.fuar?.id) === numericId ? selectedFuar : null;

    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [companyLists, setCompanyLists] = useState(EMPTY_COMPANY_LISTS);
    // #718: the per-role Kurulum rows as edited, and as last loaded (what the save compares against: only changed roles are written).
    const [kurulumRoller, setKurulumRoller] = useState(() => (isEditMode ? [] : yedekKurulumRolleri()));
    const [kurulumKayitli, setKurulumKayitli] = useState(() => (isEditMode ? [] : yedekKurulumRolleri()));
    const [logoUpload, setLogoUpload] = useState(null); // null | { fileName, fuarLogoUrl, originalName }
    const [logoUploading, setLogoUploading] = useState(false);
    const [fuarAlanlari, setFuarAlanlari] = useState([]);
    // Backlog #192: oluşturmada fuar alanının hangi salonları bağlanacak (FuarSalonlariSecici bildirir).
    const [salonSecimi, setSalonSecimi] = useState({ selected: [], all: [] });
    const [series, setSeries] = useState([]);
    const [lookupError, setLookupError] = useState(null);
    const [seriesReload, setSeriesReload] = useState(0);
    const [seriesModal, setSeriesModal] = useState(null); // null | { seriesId, initial }
    const [seriesMsg, setSeriesMsg] = useState(null);

    useEffect(() => {
        document.title = i18n.t("yonetim:fairsform.fds", { v: isEditMode ? i18n.t("yonetim:fairsform.fuariDuzenle") : i18n.t("yonetim:fairsform.yeniFuar") });
    }, [isEditMode]);

    // Edit modunda mevcut fuarı yükle
    useEffect(() => {
        if (isEditMode) dispatch(fetchFuarDetail(numericId));
    }, [isEditMode, numericId, dispatch]);

    // Fuar alanları — bir kez
    useEffect(() => {
        let ignore = false;
        async function run() {
            try {
                const rows = await fuarService.getFuarAlanlari();
                if (!ignore) setFuarAlanlari(rows);
            } catch (e) {
                if (!ignore) setLookupError(apiErrorMessage(e, i18n.t("yonetim:fairsform.fuarAlanlariYuklenemedi")));
            }
        }
        run();
        return () => { ignore = true; };
    }, []);

    // Seri listesi — seri oluşturulunca/düzenlenince yeniden
    useEffect(() => {
        let ignore = false;
        async function run() {
            try {
                const rows = await fuarService.listFuarSeriesMerged();
                if (!ignore) setSeries(rows);
            } catch (e) {
                if (!ignore) setLookupError(apiErrorMessage(e, i18n.t("yonetim:fairsform.fuarSerileriYuklenemedi")));
            }
        }
        run();
        return () => { ignore = true; };
    }, [seriesReload]);

    // Edit modunda form alanlarını doldur
    useEffect(() => {
        if (!detail) return;
        async function run() {
            const f = detail.fuar || {};
            const texts = detail.fuarTexts || [];
            const tr = texts.find((t) => Number(t.languageId) === LANGUAGE.TR) || texts[0] || {};
            const en = texts.find((t) => Number(t.languageId) === LANGUAGE.EN) || {};
            setForm({
                fuarNameTr: tr.fuarName || "",
                fuarDescTr: tr.fuarDescription || "",
                fuarNameEn: en.fuarName || "",
                fuarDescEn: en.fuarDescription || "",
                fuarStartDate: f.fuarStartDate || "",
                fuarEndDate: f.fuarEndDate || "",
                fuarDocumentsStartDate: f.fuarDocumentsStartDate || "",
                fuarDocumentsStartTime: f.fuarDocumentsStartTime?.slice(0, 5) || "09:00",
                fuarDocumentsEndDate: f.fuarDocumentsEndDate || "",
                fuarDocumentsEndTime: f.fuarDocumentsEndTime?.slice(0, 5) || "18:00",
                fuarSetupStartDate: f.fuarSetupStartDate || "",
                fuarSetupEndDate: f.fuarSetupEndDate || "",
                fuarBreakStartDate: f.fuarBreakStartDate || "",
                fuarBreakEndDate: f.fuarBreakEndDate || "",
                ...Object.fromEntries(OPTIONAL_DATETIME_FIELDS.map((name) => [name, toFormValue(name, f[name])])),
                fuarAlaniId: f.fuarAlaniId ? String(f.fuarAlaniId) : "",
                fuarSeriesId: f.fuarSeriesId ? String(f.fuarSeriesId) : "",
            });
            setErrors({});
            // Kayıttan sonra yeniden çekilen detay yüklenen logoyu zaten içeriyor.
            setLogoUpload(null);

            // #21: rol listelerini detay-new'in salt-okunur satırlarından doldur — form artık
            // bunları GÖNDERİYOR, bu yüzden ekranda gösterilenle backend'deki gerçek durum
            // eşleşmeli. seriesReadFailed=true iken bu listeler de eksik olabilir (bkz. firma
            // kartındaki uyarı) — kaydetmeden önce kullanıcı sayfayı yenileyip doğrulamalı.
            const nextLists = {};
            ROLE_GROUPS.forEach(({ readKey, formKey }) => {
                const rows = detail[readKey];
                nextLists[formKey] = Array.isArray(rows) ? rows.map(normalizeCompanyRow) : [];
            });
            setCompanyLists(nextLists);

            // #718: the role rows come from the fair's own read, so a role the backend adds later (Taşeron) appears without a change here.
            const rolSatirlari = Array.isArray(detail.kurulumRolSaatleri) ? detail.kurulumRolSaatleri.map(rolFormu) : [];
            setKurulumRoller(rolSatirlari);
            setKurulumKayitli(rolSatirlari);
        }
        run();
    }, [detail]);

    // Başarılı kayıt sonrası: yeni fuar → listeye; düzenleme → sayfada kal, yeniden çek
    useEffect(() => {
        if (!success) return undefined;
        if (isEditMode) {
            dispatch(fetchFuarDetail(numericId));
            const t = setTimeout(() => dispatch(clearSuccess()), 3500);
            return () => clearTimeout(t);
        }
        const t = setTimeout(() => {
            dispatch(clearSuccess());
            // #718: the fair exists but its role hours were refused: open it (not the list) so the refusal is read beside the Kurulum block
            // and the form is not submitted again as a NEW fair.
            navigate(kurulumHata?.fuarId ? `/Management/Fairs/${kurulumHata.fuarId}` : "/Management/Fairs");
        }, 1500);
        return () => clearTimeout(t);
    }, [success, isEditMode, numericId, kurulumHata, dispatch, navigate]);

    useEffect(() => {
        if (!error) return undefined;
        const t = setTimeout(() => dispatch(clearError()), 6000);
        return () => clearTimeout(t);
    }, [error, dispatch]);

    useEffect(() => {
        if (!seriesMsg) return undefined;
        const t = setTimeout(() => setSeriesMsg(null), 3500);
        return () => clearTimeout(t);
    }, [seriesMsg]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
        }));
        // Saat alanının hatası tarih alanında gösteriliyor — ikisini birlikte temizle.
        const pairedDate = name.endsWith("Time") ? name.replace(/Time$/, "Date") : null;
        if (errors[name] || (pairedDate && errors[pairedDate])) {
            setErrors((prev) => ({ ...prev, [name]: null, ...(pairedDate ? { [pairedDate]: null } : {}) }));
        }
    };

    const clearDigitalPeriod = () => {
        setForm((prev) => ({
            ...prev,
            [DIGITAL_PAIR.start.date]: "", [DIGITAL_PAIR.start.time]: "",
            [DIGITAL_PAIR.end.date]: "", [DIGITAL_PAIR.end.time]: "",
        }));
        setErrors((prev) => ({ ...prev, [DIGITAL_PAIR.start.date]: null, [DIGITAL_PAIR.end.date]: null }));
    };

    // Logo seçilir seçilmez yüklenir (#18: yükleme fuar kaydından ayrı); kayıtta yalnızca dönen ad gider.
    const handleLogoSelect = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // aynı dosya yeniden seçilebilsin
        if (!file) return;
        const extension = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
        let problem = null;
        if (!LOGO_EXTENSIONS.includes(extension)) problem = tx("fairsform.logoPngJpgVeyaWebp");
        else if (file.size > LOGO_MAX_BYTES) problem = "Logo en fazla 5 MB olabilir.";
        if (problem) {
            setErrors((prev) => ({ ...prev, fuarLogoFile: problem }));
            return;
        }
        setErrors((prev) => ({ ...prev, fuarLogoFile: null }));
        setLogoUploading(true);
        try {
            const uploaded = await fuarService.uploadFuarLogo(file);
            setLogoUpload({ ...uploaded, originalName: file.name });
        } catch (err) {
            setErrors((prev) => ({ ...prev, fuarLogoFile: apiErrorMessage(err, tx("fairsform.logoYuklenemedi")) }));
        } finally {
            setLogoUploading(false);
        }
    };

    // #21: FirmaSearchSelect'in `value`'sini hiç set etmiyoruz (her zaman boş arama kutusu) —
    // seçilen firma buraya eklenip kutu kendini temizliyor, böylece art arda birden çok firma
    // eklenebiliyor. Aynı firma iki kez eklenemez.
    const handleAddCompany = useCallback((formKey, firma) => {
        if (!firma) return;
        setCompanyLists((prev) => {
            const list = prev[formKey] || [];
            if (list.some((f) => Number(f.firmaId) === Number(firma.firmaId))) return prev;
            return { ...prev, [formKey]: [...list, firma] };
        });
    }, []);

    const handleRemoveCompany = useCallback((formKey, firmaId) => {
        setCompanyLists((prev) => ({
            ...prev,
            [formKey]: (prev[formKey] || []).filter((f) => Number(f.firmaId) !== Number(firmaId)),
        }));
    }, []);

    // #718: one bound of one role row; the refusal of the last save goes away as soon as the rows are touched.
    const handleKurulumRol = (firmaTypeId, alan, value) => {
        setKurulumRoller((prev) => prev.map((r) => (r.firmaTypeId === firmaTypeId ? { ...r, [alan]: value } : r)));
        setErrors((prev) => (prev[`kurulumRol${firmaTypeId}`] ? { ...prev, [`kurulumRol${firmaTypeId}`]: null } : prev));
        if (kurulumHata) dispatch(clearKurulumHata());
    };

    const handleSeriesSaved = useCallback((newId, msg) => {
        setSeriesModal(null);
        setSeriesMsg(msg);
        setSeriesReload((n) => n + 1);
        if (newId != null) setForm((f) => ({ ...f, fuarSeriesId: String(newId) }));
    }, []);

    // ── Validation ────────────────────────────────────────────────────────────
    const validate = () => {
        const e = {};
        if (!form.fuarNameTr.trim()) e.fuarNameTr = tx("fairsform.fuarAdiZorunludur");
        if (!isEditMode && !logoUpload) e.fuarLogoFile = "Fuar logosu zorunludur.";

        DATE_PAIRS.forEach((pair) => {
            const { start, end } = pair;
            if (!form[start.date]) e[start.date] = `${start.label} zorunludur.`;
            if (!form[end.date]) e[end.date] = `${end.label} zorunludur.`;
            if (form[start.date] && form[end.date] && isEndBeforeStart(form, pair)) e[end.date] = pair.orderError;
        });

        const { start, end } = DIGITAL_PAIR;
        const hasStart = Boolean(form[start.date]);
        const hasEnd = Boolean(form[end.date]);
        if (!hasStart && form[start.time]) e[start.date] = "Saat girildiyse tarih de girilmelidir.";
        if (!hasEnd && form[end.time]) e[end.date] = "Saat girildiyse tarih de girilmelidir.";
        if (hasStart !== hasEnd) {
            e[hasStart ? end.date : start.date] = tx("fairsform.baslangicVeBitisTarihiBirlikte");
        } else if (hasStart && isEndBeforeStart(form, DIGITAL_PAIR)) {
            e[end.date] = DIGITAL_PAIR.orderError;
        }

        if (!form.fuarAlaniId) e.fuarAlaniId = tx("fairsform.fuarAlaniSeciniz");

        // #718: the same two refusals the backend makes, caught before anything is saved (the fair itself is saved first, the roles after it).
        kurulumRoller.forEach((r) => {
            const sorun = pencereSorunu(r);
            if (sorun) e[`kurulumRol${r.firmaTypeId}`] = tx(sorun === "tarihsiz" ? "fairsform.kurulumRolTarihsiz" : "fairsform.kurulumRolSira", { rol: kurulumRolAdi(r) });
        });
        return e;
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = (e) => {
        e.preventDefault();
        if (logoUploading) return;
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        // multipart/form-data — her alan @RequestParam
        const fd = new FormData();
        fd.append("fuarNameTr", form.fuarNameTr.trim());
        fd.append("fuarDescTr", form.fuarDescTr);
        // EN metni boş bırakılırsa TR ile doldurulur: backend fuar_text'e boş ad yazardı.
        fd.append("fuarNameEn", form.fuarNameEn.trim() || form.fuarNameTr.trim());
        fd.append("fuarDescEn", form.fuarDescEn || form.fuarDescTr);
        // Backlog #42: fuarReportActive dropped — the backend now defaults it (New: 0, Update: keeps
        // the stored value) instead of the form always sending a value nobody edits meaningfully any
        // more (see FuarController).
        fd.append("fuarStartDate", form.fuarStartDate);
        fd.append("fuarEndDate", form.fuarEndDate);
        fd.append("fuarDocumentsStartDate", form.fuarDocumentsStartDate);
        fd.append("fuarDocumentsStartTime", toTimeParam(form.fuarDocumentsStartTime, detail?.fuar?.fuarDocumentsStartTime));
        fd.append("fuarDocumentsEndDate", form.fuarDocumentsEndDate);
        fd.append("fuarDocumentsEndTime", toTimeParam(form.fuarDocumentsEndTime, detail?.fuar?.fuarDocumentsEndTime));
        fd.append("fuarSetupStartDate", form.fuarSetupStartDate);
        fd.append("fuarSetupEndDate", form.fuarSetupEndDate);
        fd.append("fuarBreakStartDate", form.fuarBreakStartDate);
        fd.append("fuarBreakEndDate", form.fuarBreakEndDate);
        fd.append("fuarAlaniId", form.fuarAlaniId);
        // Gönderilmezse fuar seriden AYRILIR — bu bilinçli "Seri yok" seçimi demek.
        if (form.fuarSeriesId) fd.append("fuarSeriesId", form.fuarSeriesId);
        // Düzenlemede gönderilmezse mevcut logo kalır.
        if (logoUpload) fd.append("fuarLogoFile", logoUpload.fileName);

        // #17: düzenlemede yalnızca değişen alanlar ("" = temizle). Gönderilmeyen alan backend'de
        // saklı değeriyle kalır ve o çiftin sıra denetimi çalışmaz; yeni fuarda boşlar hiç gitmez.
        OPTIONAL_DATETIME_FIELDS.forEach((name) => {
            const value = form[name];
            if (isEditMode) {
                if (value !== toFormValue(name, detail?.fuar?.[name])) fd.append(name, value);
            } else if (value) {
                fd.append(name, value);
            }
        });

        // #21/#22: her rol için GÜNCEL listeyi gönderiyoruz — bu ekran artık salt-okunur değil,
        // gösterilen liste backend'e yazılan liste ile birebir eşleşmeli. Alan adı HİÇ
        // eklenmezse backend dokunmaz; boş bir değer ("") ise "mevcut liste boş" demektir ve
        // rolü TAMAMEN siler (util/knowledgebase/fuar-seri-ve-talep-turleri.md — "present-but-
        // empty = wiped", FuarService.replaceServiceFirmas). Bu form her zaman rolün o anki
        // halini temsil ettiği için boş bir rolü de bilerek gönderiyoruz.
        ROLE_GROUPS.forEach(({ formKey }) => {
            const list = companyLists[formKey] || [];
            if (list.length === 0) {
                fd.append(formKey, "");
            } else {
                list.forEach((f) => fd.append(formKey, f.firmaId));
            }
        });

        // WAS: dispatch(updateFuar({ id: numericId, formData: fd })) / dispatch(createFuar(fd)).
        // Backlog #192: oluşturmada seçilen salonlar yeni fuara bağlanır; düzenlemede fuar alanı
        // değiştiyse eski alanın standsız salonlarının bağı kaldırılır (fuarSlice).
        // #718: yalnızca DEĞİŞEN rollerin kurulum saatleri, fuar kaydedildikten sonra (fuarSlice) yazılır.
        const kurulumRolDegisimi = degisenRoller(kurulumKayitli, kurulumRoller);
        if (isEditMode) {
            const savedAlaniId = detail?.fuar?.fuarAlaniId;
            const venueChanged = savedAlaniId != null && String(savedAlaniId) !== String(form.fuarAlaniId);
            dispatch(updateFuar({
                id: numericId, formData: fd,
                venueChange: venueChanged ? { newFuarAlaniId: form.fuarAlaniId } : null,
                kurulumRoller: kurulumRolDegisimi,
            }));
        } else {
            dispatch(createFuar({ formData: fd, salonIds: salonSecimi.selected, allSalonIds: salonSecimi.all, kurulumRoller: kurulumRolDegisimi }));
        }
    };

    // ── Derived ───────────────────────────────────────────────────────────────
    const selectedSeries = series.find((s) => String(s.id) === String(form.fuarSeriesId)) || null;
    // Fuar silinmiş bir seriye bağlıysa listede yok — id'yi yine de gösterip kaybetme.
    const orphanSeriesId = form.fuarSeriesId && !selectedSeries ? form.fuarSeriesId : null;
    const isSelectedFair = isEditMode && Number(selectedFuarId) === numericId;
    const pageTitle = isEditMode
        ? (detail?.fuarTexts?.find((t) => Number(t.languageId) === LANGUAGE.TR)?.fuarName || tx("fairsform.fuariDuzenle"))
        : tx("fairsform.yeniFuarEkle");
    const hasDigitalPeriod = [DIGITAL_PAIR.start, DIGITAL_PAIR.end].some((f) => form[f.date] || form[f.time]);
    // #718: the refusal of the last role-hours save, only on the fair it belongs to; an empty role shows the fair's own Kurulum date as its placeholder.
    const kurulumHataMetni = kurulumHata && Number(kurulumHata.fuarId) === numericId ? kurulumHata.message : null;
    const kurulumYerTutucu = (tarih) => (tarih ? tarihSaatMetni({ tarih }) : undefined);

    let logoCaption;
    if (logoUpload) logoCaption = tx("fairsform.yeniLogoKaydedinceUygulanir", { originalName: logoUpload.originalName });
    else if (!isEditMode) logoCaption = tx("fairsform.henuzLogoYuklenmediVarsayilanGorsel");
    else logoCaption = LOGO_SOURCE_LABELS[detail?.fuarLogoSource] || null;

    const renderDateTimeField = ({ date, time, label }, required) => {
        const message = errors[date] || errors[time];
        return (
            <Col md={6} key={date}>
                <Label for={date}>
                    {label}{required && <span className="text-danger"> *</span>}
                </Label>
                {/* #560: tarih alanı bir sarmalayıcı içinde olduğundan InputGroup yerine yan yana iki kutu. */}
                <div className="d-flex gap-2">
                    <TarihGirdisi
                        id={date} className="flex-grow-1" bsSize={null}
                        value={form[date]} onChange={(v) => handleChange({ target: { name: date, value: v } })} invalid={!!errors[date]}
                    />
                    <Input
                        id={time} type="time" name={time} aria-label={tx("fairsform.saati", { label })}
                        value={form[time]} onChange={handleChange} invalid={!!errors[time]}
                        style={{ maxWidth: 130 }}
                    />
                </div>
                {message && <div className="invalid-feedback d-block">{message}</div>}
            </Col>
        );
    };

    // #718: one bound (date + time) of one role row.
    const renderKurulumRolSiniri = (r, tarihAlani, saatAlani, etiket, yerTutucu) => {
        const alanId = `kurulumRol${r.firmaTypeId}${tarihAlani}`;
        return (
            <Col md={6}>
                <Label className="small mb-1" for={alanId}>{etiket}</Label>
                <div className="d-flex gap-2">
                    <TarihGirdisi
                        id={alanId} className="flex-grow-1" bsSize={null} yerTutucuMetni={yerTutucu}
                        value={r[tarihAlani]} onChange={(v) => handleKurulumRol(r.firmaTypeId, tarihAlani, v)}
                        invalid={!!errors[`kurulumRol${r.firmaTypeId}`]}
                    />
                    <Input
                        type="time" aria-label={tx("fairsform.saati", { label: etiket })}
                        value={r[saatAlani]} onChange={(e) => handleKurulumRol(r.firmaTypeId, saatAlani, e.target.value)}
                        invalid={!!errors[`kurulumRol${r.firmaTypeId}`]} style={{ maxWidth: 130 }}
                    />
                </div>
            </Col>
        );
    };

    const renderDateTimePair = (pair, required) => (
        <Row className="mb-3 g-3 align-items-start" key={pair.start.date}>
            {renderDateTimeField(pair.start, required)}
            {renderDateTimeField(pair.end, required)}
        </Row>
    );

    // ── Render ────────────────────────────────────────────────────────────────
    if (isEditMode && detailLoading && !detail) {
        return (
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={tx("fairsform.fuariDuzenle")} pageTitle={tx("fairsform.fuarlar")} />
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title={pageTitle} pageTitle={tx("fairsform.fuarlar")} />

                {error && <Alert color="danger">{error}</Alert>}
                {success && (
                    <Alert color="success">
                        {success}{!isEditMode && tx("fairsform.yonlendiriliyor")}
                    </Alert>
                )}
                {lookupError && <Alert color="warning">{lookupError}</Alert>}

                {isEditMode && (
                    <Card className="mb-3">
                        <CardBody className="py-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <Badge color="light" className="text-dark border">{tx("fairsform.fuar")}{numericId}</Badge>
                                {isSelectedFair
                                    ? <Badge color="success"><i className="bx bx-check-circle me-1" />{tx("fairsform.seciliFuar")}</Badge>
                                    : <span className="text-muted small">{tx("fairsform.ustMenudeSeciliFuarBu")}</span>}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                {isSelectedFair && (
                                    <Link to="/Management/Fairs/Rules" className="btn btn-soft-primary btn-sm">
                                        <i className="bx bx-shield-quarter me-1" />{tx("fairsform.buFuarinKurallari")}
                                    </Link>
                                )}
                                {/* #550: only an EMPTY fair can be deleted (GET /fuar/{id}/silme-kontrolu says what is still on it); the fair
                                    selected in the top bar is never offered — the user's own selection would point at a deleted fair. */}
                                <KontrolluSilDugmesi
                                    key={numericId} tur="fuar" ad={pageTitle}
                                    engel={isSelectedFair ? tx("components:kaliciSil.seciliFuar") : null}
                                    kontrol={() => fuarService.getFuarSilmeKontrolu(numericId)}
                                    sil={() => fuarService.deleteFuar(numericId)}
                                    onSilindi={() => navigate("/Management/Fairs")}
                                />
                            </div>
                        </CardBody>
                    </Card>
                )}

                <form onSubmit={handleSubmit} noValidate>

                    {/* ── CARD 1: Genel Bilgiler (logo dahil) ── */}
                    <Card className="mb-4">
                        <CardHeader className="border-bottom">
                            <h5 className="mb-0">
                                <i className="bx bx-info-circle me-2 text-primary"></i>
                                {tx("fairsform.genelBilgiler")}
                            </h5>
                        </CardHeader>
                        <CardBody>
                            <Row className="g-4">
                                <Col lg={3}>
                                    <Label for="fuarLogoInput">
                                        {tx("fairsform.fuarLogosu")}{" "}{!isEditMode && <span className="text-danger">*</span>}
                                    </Label>
                                    <div
                                        className={`border rounded bg-light d-flex align-items-center justify-content-center ${errors.fuarLogoFile ? "border-danger" : ""}`}
                                        style={{ height: 140 }}
                                    >
                                        {logoUploading ? (
                                            <Spinner color="primary" />
                                        ) : (
                                            <FuarLogoImg
                                                url={logoUpload ? logoUpload.fuarLogoUrl : detail?.fuarLogoUrl}
                                                fileName={logoUpload ? null : detail?.fuar?.fuarLogo}
                                                alt={tx("fairsform.fuarLogosu2")}
                                                style={{ maxHeight: 120, maxWidth: "100%", objectFit: "contain" }}
                                            />
                                        )}
                                    </div>
                                    {logoCaption && <div className="text-muted small mt-1">{logoCaption}</div>}
                                    <Input
                                        id="fuarLogoInput" type="file" bsSize="sm" className="mt-2"
                                        accept={LOGO_ACCEPT} onChange={handleLogoSelect}
                                        disabled={logoUploading || loading} invalid={!!errors.fuarLogoFile}
                                    />
                                    <FormFeedback>{errors.fuarLogoFile}</FormFeedback>
                                    <div className="text-muted fs-12 mt-1">
                                        {tx("fairsform.pngJpgVeyaWebpEn")}
                                        {isEditMode && tx("fairsform.yeniLogoSecilmezseMevcutLogo")}
                                    </div>
                                    {logoUpload && (
                                        <Button
                                            color="link" size="sm" type="button" className="p-0 mt-1"
                                            onClick={() => setLogoUpload(null)}
                                        >
                                            <i className="bx bx-undo me-1" />
                                            {isEditMode ? tx("fairsform.mevcutLogoyaDon") : tx("fairsform.yuklenenLogoyuKaldir")}
                                        </Button>
                                    )}
                                </Col>
                                <Col lg={9}>
                                    <Row>
                                        {/* Backlog #42: WAS a Col md={9} sharing this row with a "Raporlarda" switch
                                            (Col md={3}, fuarReportActive) — removed; report-active had its own help
                                            text saying it was unrelated to the selected fair and several fairs could
                                            be active at once, and its only other reader was FuarListPage.jsx's now
                                            also-removed report filter. Widened to md={12} to fill the row alone. */}
                                        <Col md={12}>
                                            <FormGroup>
                                                <Label for="fuarNameTr">{tx("fairsform.fuarAdiTr")}{" "}<span className="text-danger">*</span></Label>
                                                <Input
                                                    id="fuarNameTr" name="fuarNameTr" value={form.fuarNameTr}
                                                    onChange={handleChange} invalid={!!errors.fuarNameTr}
                                                    placeholder={tx("fairsform.ornEmitt2026")}
                                                />
                                                <FormFeedback>{errors.fuarNameTr}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                        <Col md={12}>
                                            <FormGroup>
                                                <Label for="fuarDescTr">{tx("fairsform.aciklamaTr")}</Label>
                                                <Input
                                                    id="fuarDescTr" type="textarea" name="fuarDescTr" rows={2}
                                                    value={form.fuarDescTr} onChange={handleChange}
                                                    placeholder={tx("fairsform.fuarHakkindaKisaBirAciklama")}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={12}>
                                            <FormGroup>
                                                <Label for="fuarNameEn">{tx("fairsform.fairNameEn")}</Label>
                                                <Input
                                                    id="fuarNameEn" name="fuarNameEn" value={form.fuarNameEn}
                                                    onChange={handleChange} placeholder={tx("fairsform.bosBirakilirsaTurkceAdKullanilir")}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={12}>
                                            <FormGroup className="mb-0">
                                                <Label for="fuarDescEn">{tx("fairsform.descriptionEn")}</Label>
                                                <Input
                                                    id="fuarDescEn" type="textarea" name="fuarDescEn" rows={2}
                                                    value={form.fuarDescEn} onChange={handleChange}
                                                    placeholder={tx("fairsform.bosBirakilirsaTurkceAciklamaKullanilir")}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    {/* ── CARD 2: Fuar Alanı ── */}
                    <Card className="mb-4">
                        <CardHeader className="border-bottom">
                            <h5 className="mb-0">
                                <i className="bx bx-map me-2 text-primary"></i>
                                {tx("fairsform.fuarAlani")}
                            </h5>
                        </CardHeader>
                        <CardBody>
                            <Row className="align-items-center">
                                <Col md={2} className="text-md-end">
                                    <Label className="mb-0" for="fuarAlaniId">{tx("fairsform.fuarAlani")}{" "}<span className="text-danger">*</span></Label>
                                </Col>
                                <Col md={10}>
                                    <Input
                                        id="fuarAlaniId" type="select" name="fuarAlaniId"
                                        value={form.fuarAlaniId} onChange={handleChange} invalid={!!errors.fuarAlaniId}
                                    >
                                        <option value="">{tx("fairsform.seciniz")}</option>
                                        {fuarAlanlari.map((alan) => (
                                            <option key={alan.id} value={alan.id}>{alan.fuarAlaniName}</option>
                                        ))}
                                    </Input>
                                    <FormFeedback>{errors.fuarAlaniId}</FormFeedback>
                                </Col>
                            </Row>
                            {/* Backlog #192: seçilen fuar alanının salonları ve bu fuarda aktif/pasif durumları. */}
                            <Row className="mt-3">
                                <Col md={{ size: 10, offset: 2 }}>
                                    <FuarSalonlariSecici
                                        fuarAlaniId={form.fuarAlaniId || null}
                                        fuarId={isEditMode ? numericId : null}
                                        savedFuarAlaniId={detail?.fuar?.fuarAlaniId ?? null}
                                        onSelectionChange={(selected, all) => setSalonSecimi({ selected, all })}
                                    />
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    {/* ── CARD 3: Fuar Tarih ve Saat Bilgileri (backlog #47) ──
                        WAS: one card, DATE_PAIRS.map over all four pairs flat (Açılış/Kapanış, Belge
                        Kabul, Kurulum, Söküm) — Belge Kabul now lives in Card 4 below with the digital
                        period, since both are really about document acceptance rather than the fair
                        itself. This card keeps only the three sub-groups actually about the fair's own
                        physical timeline: Fuar / Kurulum / Söküm. */}
                    <Card className="mb-4">
                        <CardHeader className="border-bottom">
                            <h5 className="mb-0">
                                <i className="bx bx-calendar me-2 text-primary"></i>
                                {tx("fairsform.fuarTarihVeSaatBilgileri")}
                            </h5>
                        </CardHeader>
                        <CardBody>
                            <div className="fw-semibold small text-muted mb-2">{tx("fairsform.fuar2")}</div>
                            {renderDateTimePair(DATE_PAIR_BY_KEY.fuar, true)}
                            <div className="fw-semibold small text-muted mb-2 mt-3">{tx("fairsform.kurulum")}</div>
                            {renderDateTimePair(DATE_PAIR_BY_KEY.kurulum, true)}
                            {/* #718: Katılımcı / Dekoratör / Tedarikçi — isteğe bağlı, boş bırakılan rol yukarıdaki Kurulum'u kullanır. */}
                            {kurulumRoller.length > 0 && (
                                <div className="border rounded p-2 mb-2" data-testid="kurulum-rol-saatleri">
                                    <div className="small text-muted mb-2">{tx("fairsform.kurulumRolBaslik")}</div>
                                    {kurulumRoller.map((r) => {
                                        const rolAdi = kurulumRolAdi(r);
                                        const mesaj = errors[`kurulumRol${r.firmaTypeId}`];
                                        return (
                                            <Row className="g-2 align-items-start mb-2" key={r.firmaTypeId} data-testid={`kurulum-rol-${r.firmaTypeId}`}>
                                                {renderKurulumRolSiniri(r, "baslangicTarihi", "baslangicSaati", tx("fairsform.kurulumRolBaslangici", { rol: rolAdi }), kurulumYerTutucu(form.fuarSetupStartDate))}
                                                {renderKurulumRolSiniri(r, "bitisTarihi", "bitisSaati", tx("fairsform.kurulumRolBitisi", { rol: rolAdi }), kurulumYerTutucu(form.fuarSetupEndDate))}
                                                {mesaj && <Col xs={12}><div className="invalid-feedback d-block">{mesaj}</div></Col>}
                                            </Row>
                                        );
                                    })}
                                    {kurulumHataMetni && (
                                        <div className="invalid-feedback d-block" data-testid="kurulum-rol-hata">{kurulumHataMetni}</div>
                                    )}
                                </div>
                            )}
                            <div className="fw-semibold small text-muted mb-2 mt-3">{tx("fairsform.sokum")}</div>
                            {renderDateTimePair(DATE_PAIR_BY_KEY.sokum, true)}
                            <div className="text-muted fs-12">
                                {tx("fairsform.tarihlerZorunludurAcilisKapanisKurulum")}
                            </div>
                        </CardBody>
                    </Card>

                    {/* ── CARD 4: Belge Kabul Tarih ve Saat Bilgileri (backlog #47, reworks #17's card) ──
                        Sistem İçi = bugünkü Belge Kabul (fuar_documents_*): zorunlu, proje yüklemelerini
                        kapatır/açar. Sistem Dışı = #17'nin dijital dönemi (WhatsApp/e-posta vb.): isteğe
                        bağlı, bilgi amaçlı, Temizle düğmesi korunuyor. Türkçe "Sistem İçi"/"Sistem Dışı"
                        ifadesi görevin kendi önerisi — Erdinç/Deniz ile ayrıca teyit edilmedi. */}
                    <Card className="mb-4">
                        <CardHeader className="border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <h5 className="mb-0">
                                <i className="bx bx-envelope me-2 text-primary"></i>
                                {tx("fairsform.belgeKabulTarihVeSaat")}
                            </h5>
                            {hasDigitalPeriod && (
                                <Button color="link" size="sm" type="button" className="p-0" onClick={clearDigitalPeriod}>
                                    <i className="bx bx-eraser me-1" />{tx("fairsform.temizle")}
                                </Button>
                            )}
                        </CardHeader>
                        <CardBody>
                            <div className="fw-semibold small text-muted mb-2">{tx("fairsform.sistemIci")}</div>
                            {renderDateTimePair(DATE_PAIR_BY_KEY.belgeKabul, true)}
                            <div className="text-muted fs-12 mb-3">
                                {tx("fairsform.zorunludurSaatBosBirakilirsa00")}
                            </div>
                            <div className="fw-semibold small text-muted mb-2 mt-3">{tx("fairsform.sistemDisi")}</div>
                            {renderDateTimePair(DIGITAL_PAIR, false)}
                            <div className="text-muted fs-12">
                                {tx("fairsform.whatsAppEPostaVbYollarla")}
                            </div>
                        </CardBody>
                    </Card>

                    {/* ── CARD 5: Fuar Firmaları (backlog #21 — artık düzenlenebilir) ── */}
                    <Card className="mb-4">
                        <CardHeader className="border-bottom">
                            <h5 className="mb-0">
                                <i className="bx bx-buildings me-2 text-primary"></i>
                                {tx("fairsform.fuarFirmalari")}
                            </h5>
                        </CardHeader>
                        <CardBody>
                            {isEditMode && detail?.seriesReadFailed && (
                                <Alert color="warning" className="small">
                                    <i className="bx bx-error me-1" />
                                    {tx("fairsform.buFuarinSeriDetayiBackend", { detayNewError: detail.detayNewError })}
                                </Alert>
                            )}
                            <Row className="g-3">
                                {ROLE_GROUPS.map(({ formKey, firmaTypeId, label }) => {
                                    const list = companyLists[formKey] || [];
                                    return (
                                        <Col md={6} lg={4} key={formKey}>
                                            <div className="border rounded p-2 h-100 d-flex flex-column gap-2">
                                                <div className="fw-semibold small">{label}</div>
                                                <FirmaSearchSelect
                                                    forceLive
                                                    firmaTypeId={firmaTypeId}
                                                    placeholder={tx("fairsform.firmaEkle")}
                                                    bsSize="sm"
                                                    onChange={(firma) => handleAddCompany(formKey, firma)}
                                                />
                                                {list.length === 0 ? (
                                                    <span className="text-muted small">{tx("fairsform.henuzFirmaEklenmedi")}</span>
                                                ) : (
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {list.map((f) => (
                                                            <Badge
                                                                key={f.firmaId} color="light"
                                                                className="text-dark border d-inline-flex align-items-center gap-1"
                                                            >
                                                                {f.firmaShortname || f.firmaName || `#${f.firmaId}`}
                                                                <i
                                                                    role="button" className="bx bx-x"
                                                                    onClick={() => handleRemoveCompany(formKey, f.firmaId)}
                                                                    title={tx("fairsform.kaldir")}
                                                                />
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </Col>
                                    );
                                })}
                            </Row>
                            <div className="text-muted fs-12 mt-2">
                                {tx("fairsform.ayniFirmaBirdenCokRole")}
                            </div>
                        </CardBody>
                    </Card>

                    {/* ── CARD 6: Fuar Serisi (şimdilik en altta) ── */}
                    <Card className="mb-4">
                        <CardHeader className="border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <h5 className="mb-0">
                                <i className="bx bx-collection me-2 text-primary"></i>
                                {tx("fairsform.fuarSerisi")}
                            </h5>
                            <Link to="/Management/Fairs/Series" className="btn btn-link btn-sm p-0">
                                {tx("fairsform.tumSeriler")}{" "}<i className="bx bx-right-arrow-alt" />
                            </Link>
                        </CardHeader>
                        <CardBody>
                            {seriesMsg && <Alert color="success" className="py-2">{seriesMsg}</Alert>}
                            {detail?.seriesReadFailed && (
                                <Alert color="warning" className="small">
                                    <i className="bx bx-error me-1" />
                                    {tx("fairsform.buFuarinSeriDetayiBackend2", { detayNewError: detail.detayNewError })}
                                </Alert>
                            )}
                            <Row className="align-items-end g-2">
                                <Col md={6}>
                                    <Label for="fuarSeriesId">{tx("fairsform.buFuarinAitOlduguSeri")}</Label>
                                    <Input
                                        id="fuarSeriesId" type="select" name="fuarSeriesId"
                                        value={form.fuarSeriesId} onChange={handleChange}
                                    >
                                        <option value="">{tx("fairsform.seriYok")}</option>
                                        {orphanSeriesId && (
                                            <option value={orphanSeriesId}>{tx("fairsform.seriListedeYokSilinmis", { orphanSeriesId })}</option>
                                        )}
                                        {series.map((s) => (
                                            <option key={s.id} value={s.id}>
                                                {s.nameTr || s.nameEn || tx("fairsform.seri", { id: s.id })}
                                            </option>
                                        ))}
                                    </Input>
                                </Col>
                                <Col md="auto">
                                    <Button
                                        color="primary" outline type="button"
                                        onClick={() => setSeriesModal({ seriesId: null, initial: null })}
                                    >
                                        <i className="bx bx-plus me-1" />{tx("fairsform.yeniSeri")}
                                    </Button>
                                </Col>
                                <Col md="auto">
                                    <Button
                                        color="secondary" outline type="button" disabled={!selectedSeries}
                                        onClick={() => setSeriesModal({ seriesId: selectedSeries.id, initial: selectedSeries })}
                                    >
                                        <i className="bx bx-edit me-1" />{tx("fairsform.seriyiDuzenle")}
                                    </Button>
                                </Col>
                            </Row>
                            {selectedSeries && (
                                <div className="mt-3 small text-muted d-flex flex-wrap gap-3">
                                    {selectedSeries.nameEn && <span><span className="fw-semibold">{tx("fairsform.en")}</span> {selectedSeries.nameEn}</span>}
                                    {selectedSeries.link && (
                                        <a href={selectedSeries.link} target="_blank" rel="noreferrer">
                                            <i className="bx bx-link-external me-1" />{selectedSeries.link}
                                        </a>
                                    )}
                                </div>
                            )}
                            <div className="text-muted fs-12 mt-2">
                                {tx("fairsform.seriYokSecipKaydetmekFuari")}
                            </div>
                        </CardBody>
                    </Card>

                    {/* ── Action Buttons ── */}
                    <div className="d-flex justify-content-end gap-2 mb-4">
                        <Button color="light" type="button" onClick={() => navigate("/Management/Fairs")} disabled={loading}>
                            <i className="bx bx-x me-1"></i> {isEditMode ? tx("fairsform.listeyeDon") : tx("common:iptal")}
                        </Button>
                        <Button color="primary" type="submit" disabled={loading || logoUploading}>
                            {loading ? (
                                <><Spinner size="sm" className="me-1" />{" "}{tx("fairsform.kaydediliyor")}</>
                            ) : (
                                <><i className="bx bx-save me-1"></i> {isEditMode ? tx("fairsform.guncelle") : tx("common:kaydet")}</>
                            )}
                        </Button>
                    </div>
                </form>

                <FuarSeriesModal
                    isOpen={Boolean(seriesModal)}
                    toggle={() => setSeriesModal(null)}
                    seriesId={seriesModal?.seriesId ?? null}
                    initial={seriesModal?.initial ?? null}
                    onSaved={handleSeriesSaved}
                />
            </Container>
        </div>
    );
};

export default FairForm;
